import React, { useEffect, useState } from "react";
import api from "../../../api";
import { IMAGE_URL } from "../../../path";
import {
    Blog,
    BlogCategory,
    BlogComponent,
} from "../../../Blog";
import RichTextEditor from "./RichTextEditor";

interface BlogModalProps {
    blog: Blog | null;
    categories: BlogCategory[];
    onClose: () => void;
    onSaved: () => void;
}

const BlogModal: React.FC<BlogModalProps> = ({
                                                 blog,
                                                 categories,
                                                 onClose,
                                                 onSaved,
                                             }) => {
    const [categoryId, setCategoryId] = useState("");
    const [title, setTitle] = useState("");
    const [subtitle, setSubtitle] = useState("");
    const [readTime, setReadTime] = useState(1);

    const [previewImage, setPreviewImage] =
        useState<File | null>(null);

    const [previewImagePreview, setPreviewImagePreview] =
        useState<string | null>(null);

    const [heroImage, setHeroImage] =
        useState<File | null>(null);

    const [heroImagePreview, setHeroImagePreview] =
        useState<string | null>(null);

    const [active, setActive] = useState(true);
    const [displayOrder, setDisplayOrder] = useState(0);

    const [components, setComponents] =
        useState<BlogComponent[]>([]);

    const [componentType, setComponentType] =
        useState<"image" | "body">("body");

    const [componentImage, setComponentImage] =
        useState<File | null>(null);

    const [componentImagePreview, setComponentImagePreview] =
        useState<string | null>(null);

    const [componentBody, setComponentBody] = useState("");

    const [editingComponentId, setEditingComponentId] =
        useState<number | null>(null);

    const [saving, setSaving] = useState(false);

    const [savingComponent, setSavingComponent] =
        useState(false);

    const [deletingComponentId, setDeletingComponentId] =
        useState<number | null>(null);

    const [movingComponentId, setMovingComponentId] =
        useState<number | null>(null);

    const [error, setError] = useState("");

    useEffect(() => {
        if (blog) {
            setCategoryId(
                blog.blog_category_id.toString()
            );

            setTitle(blog.title);
            setSubtitle(blog.subtitle || "");
            setReadTime(blog.read_time);
            setActive(blog.active);
            setDisplayOrder(blog.display_order);

            if (blog.preview_image) {
                setPreviewImagePreview(
                    `${IMAGE_URL}/${blog.preview_image}`
                );
            } else {
                setPreviewImagePreview(null);
            }

            if (blog.hero_image) {
                setHeroImagePreview(
                    `${IMAGE_URL}/${blog.hero_image}`
                );
            } else {
                setHeroImagePreview(null);
            }

            setPreviewImage(null);
            setHeroImage(null);

            loadComponents(blog.id);
        } else {
            setCategoryId(
                categories.length > 0
                    ? categories[0].id.toString()
                    : ""
            );

            setTitle("");
            setSubtitle("");
            setReadTime(1);

            setPreviewImage(null);
            setPreviewImagePreview(null);

            setHeroImage(null);
            setHeroImagePreview(null);

            setActive(true);
            setDisplayOrder(0);
            setComponents([]);
        }

        resetComponentForm();
        setError("");
    }, [blog, categories]);

    const loadComponents = async (blogId: number) => {
        try {
            const response = await api.get(
                `/admin/blog-components?blog_id=${blogId}`
            );

            const loaded =
                response.data.data ??
                response.data;

            setComponents(
                [...loaded].sort(
                    (a: BlogComponent, b: BlogComponent) =>
                        a.display_order - b.display_order ||
                        a.id - b.id
                )
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to load blog components."
            );
        }
    };

    const resetComponentForm = () => {
        setComponentType("body");
        setComponentImage(null);
        setComponentImagePreview(null);
        setComponentBody("");
        setEditingComponentId(null);
    };

    /*
     * =========================================================
     * BLOG IMAGES
     * =========================================================
     */

    const handlePreviewImageChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0] || null;

        setPreviewImage(file);

        if (file) {
            setPreviewImagePreview(
                URL.createObjectURL(file)
            );
        }
    };

    const handleHeroImageChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0] || null;

        setHeroImage(file);

        if (file) {
            setHeroImagePreview(
                URL.createObjectURL(file)
            );
        }
    };

    /*
     * =========================================================
     * BLOG SAVE
     * =========================================================
     */

    const handleSave = async () => {
        if (!categoryId) {
            setError("Please select a category.");
            return;
        }

        if (!title.trim()) {
            setError("Please enter a blog title.");
            return;
        }

        if (readTime < 1) {
            setError("Read time must be at least 1 minute.");
            return;
        }

        if (!blog && !previewImage) {
            setError("Please select a preview image.");
            return;
        }

        if (!blog && !heroImage) {
            setError("Please select a hero image.");
            return;
        }

        try {
            setSaving(true);
            setError("");

            const formData = new FormData();

            formData.append(
                "blog_category_id",
                categoryId
            );

            formData.append(
                "title",
                title.trim()
            );

            formData.append(
                "subtitle",
                subtitle
            );

            formData.append(
                "read_time",
                readTime.toString()
            );

            formData.append(
                "active",
                active ? "1" : "0"
            );

            formData.append(
                "display_order",
                displayOrder.toString()
            );

            if (previewImage) {
                formData.append(
                    "preview_image",
                    previewImage
                );
            }

            if (heroImage) {
                formData.append(
                    "hero_image",
                    heroImage
                );
            }

            if (blog) {
                formData.append("_method", "PUT");

                await api.post(
                    `/admin/blogs/${blog.id}`,
                    formData
                );
            } else {
                const response = await api.post(
                    "/admin/blogs",
                    formData
                );

                const createdBlog =
                    response.data.data;

                if (createdBlog?.id) {
                    onSaved();
                    return;
                }
            }

            onSaved();
        } catch (err: any) {
            console.error(err);

            const validationErrors =
                err?.response?.data?.errors;

            if (validationErrors) {
                const firstError = Object.values(
                    validationErrors
                )[0] as string[];

                setError(
                    firstError?.[0] ||
                    "Validation failed."
                );
            } else {
                setError(
                    err?.response?.data?.message ||
                    "Failed to save blog."
                );
            }
        } finally {
            setSaving(false);
        }
    };

    /*
     * =========================================================
     * COMPONENT IMAGE
     * =========================================================
     */

    const handleComponentImageChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0] || null;

        setComponentImage(file);

        if (file) {
            setComponentImagePreview(
                URL.createObjectURL(file)
            );
        } else {
            setComponentImagePreview(null);
        }
    };

    /*
     * =========================================================
     * ADD / EDIT COMPONENT
     * =========================================================
     */

    const handleEditComponent = (
        component: BlogComponent
    ) => {
        setEditingComponentId(component.id);
        setComponentType(component.type);
        setComponentBody(component.body || "");

        if (component.image) {
            setComponentImagePreview(
                `${IMAGE_URL}/${component.image}`
            );
        } else {
            setComponentImagePreview(null);
        }

        setComponentImage(null);
        setError("");
    };

    const handleSaveComponent = async () => {
        if (!blog) {
            setError(
                "Save the blog before adding components."
            );
            return;
        }

        if (
            componentType === "image" &&
            !componentImage &&
            !componentImagePreview
        ) {
            setError(
                "Please select an image for the component."
            );
            return;
        }

        if (
            componentType === "body" &&
            !componentBody.trim()
        ) {
            setError(
                "Please enter body content."
            );
            return;
        }

        try {
            setSavingComponent(true);
            setError("");

            const formData = new FormData();

            formData.append(
                "blog_id",
                blog.id.toString()
            );

            formData.append(
                "type",
                componentType
            );

            if (componentType === "image") {
                if (componentImage) {
                    formData.append(
                        "image",
                        componentImage
                    );
                }
            } else {
                formData.append(
                    "body",
                    componentBody
                );
            }

            if (editingComponentId) {
                formData.append("_method", "PUT");

                await api.post(
                    `/admin/blog-components/${editingComponentId}`,
                    formData
                );
            } else {
                formData.append(
                    "display_order",
                    components.length.toString()
                );

                await api.post(
                    "/admin/blog-components",
                    formData
                );
            }

            await loadComponents(blog.id);

            resetComponentForm();
        } catch (err: any) {
            console.error(err);

            const validationErrors =
                err?.response?.data?.errors;

            if (validationErrors) {
                const firstError = Object.values(
                    validationErrors
                )[0] as string[];

                setError(
                    firstError?.[0] ||
                    "Validation failed."
                );
            } else {
                setError(
                    err?.response?.data?.message ||
                    "Failed to save blog component."
                );
            }
        } finally {
            setSavingComponent(false);
        }
    };

    /*
     * =========================================================
     * DELETE COMPONENT
     * =========================================================
     */

    const handleDeleteComponent = async (
        component: BlogComponent
    ) => {
        const confirmed = window.confirm(
            "Are you sure you want to delete this component?"
        );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingComponentId(component.id);
            setError("");

            await api.delete(
                `/admin/blog-components/${component.id}`
            );

            await loadComponents(blog!.id);

            if (
                editingComponentId === component.id
            ) {
                resetComponentForm();
            }
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to delete blog component."
            );
        } finally {
            setDeletingComponentId(null);
        }
    };

    /*
     * =========================================================
     * MOVE COMPONENT
     * =========================================================
     */

    const handleMoveComponent = async (
        component: BlogComponent,
        direction: "up" | "down"
    ) => {
        if (!blog) {
            return;
        }

        try {
            setMovingComponentId(component.id);
            setError("");

            await api.post(
                `/admin/blog-components/${component.id}/move`,
                {
                    direction,
                }
            );

            await loadComponents(blog.id);
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to move blog component."
            );
        } finally {
            setMovingComponentId(null);
        }
    };

    const canMoveComponentUp = (
        index: number
    ) => {
        return index > 0;
    };

    const canMoveComponentDown = (
        index: number
    ) => {
        return index < components.length - 1;
    };

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal admin-blog-modal">
                <div className="admin-modal-header">
                    <h2>
                        {blog
                            ? "Edit Blog"
                            : "Add Blog"}
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                <div className="admin-modal-content">
                    {error && (
                        <div className="form-error">
                            {error}
                        </div>
                    )}

                    {/* =================================================
                        BLOG DETAILS
                    ================================================= */}

                    <div className="form-group">
                        <label htmlFor="blog-category">
                            Category
                        </label>

                        <select
                            id="blog-category"
                            value={categoryId}
                            onChange={(e) =>
                                setCategoryId(
                                    e.target.value
                                )
                            }
                        >
                            <option value="">
                                Select a category
                            </option>

                            {categories.map(
                                (category) => (
                                    <option
                                        key={category.id}
                                        value={category.id}
                                    >
                                        {category.name}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="blog-title">
                            Title
                        </label>

                        <input
                            id="blog-title"
                            type="text"
                            value={title}
                            onChange={(e) =>
                                setTitle(e.target.value)
                            }
                            placeholder="Enter blog title"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="blog-subtitle">
                            Subtitle
                        </label>

                        <textarea
                            id="blog-subtitle"
                            value={subtitle}
                            onChange={(e) =>
                                setSubtitle(
                                    e.target.value
                                )
                            }
                            placeholder="Enter blog subtitle"
                            rows={3}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="blog-read-time">
                            Read Time (minutes)
                        </label>

                        <input
                            id="blog-read-time"
                            type="number"
                            min="1"
                            value={readTime}
                            onChange={(e) =>
                                setReadTime(
                                    Number(e.target.value)
                                )
                            }
                        />
                    </div>

                    {/* PREVIEW IMAGE */}

                    <div className="form-group">
                        <label htmlFor="blog-preview-image">
                            Preview Image
                        </label>

                        <input
                            id="blog-preview-image"
                            type="file"
                            accept="image/*,.svg"
                            onChange={
                                handlePreviewImageChange
                            }
                        />

                        {previewImagePreview && (
                            <div className="admin-image-preview">
                                <img
                                    src={
                                        previewImagePreview
                                    }
                                    alt="Blog preview"
                                    style={{
                                        width: "160px",
                                        height: "100px",
                                        objectFit: "cover",
                                        borderRadius: "6px",
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* HERO IMAGE */}

                    <div className="form-group">
                        <label htmlFor="blog-hero-image">
                            Hero Image
                        </label>

                        <input
                            id="blog-hero-image"
                            type="file"
                            accept="image/*,.svg"
                            onChange={
                                handleHeroImageChange
                            }
                        />

                        {heroImagePreview && (
                            <div className="admin-image-preview">
                                <img
                                    src={
                                        heroImagePreview
                                    }
                                    alt="Blog hero"
                                    style={{
                                        width: "160px",
                                        height: "100px",
                                        objectFit: "cover",
                                        borderRadius: "6px",
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label className="admin-checkbox-label">
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(e) =>
                                    setActive(
                                        e.target.checked
                                    )
                                }
                            />
                            Active
                        </label>
                    </div>

                    <div className="form-group">
                        <label htmlFor="blog-display-order">
                            Display Order
                        </label>

                        <input
                            id="blog-display-order"
                            type="number"
                            min="0"
                            value={displayOrder}
                            onChange={(e) =>
                                setDisplayOrder(
                                    Number(e.target.value)
                                )
                            }
                        />
                    </div>

                    <div className="admin-modal-actions">
                        <button
                            type="button"
                            className="admin-table-button"
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="admin-primary-button"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving
                                ? "Saving..."
                                : "Save Changes"}
                        </button>
                    </div>

                    {/* =================================================
                        COMPONENTS
                    ================================================= */}

                    {blog && (
                        <>
                            <hr />

                            <div className="admin-page-header">
                                <div>
                                    <h3>
                                        Blog Components
                                    </h3>

                                    <p>
                                        Add and reorder the
                                        content that makes up
                                        this blog.
                                    </p>
                                </div>
                            </div>

                            <div className="admin-blog-components">
                                {components.length === 0 ? (
                                    <p>
                                        No components added yet.
                                    </p>
                                ) : (
                                    components.map(
                                        (
                                            component,
                                            index
                                        ) => (
                                            <div
                                                key={
                                                    component.id
                                                }
                                                className="admin-blog-component"
                                            >
                                                <div className="admin-blog-component-preview">
                                                    <strong>
                                                        {component.type ===
                                                        "image"
                                                            ? "Image"
                                                            : "Body"}
                                                    </strong>

                                                    {component.type ===
                                                    "image" ? (
                                                        component.image ? (
                                                            <img
                                                                src={`${IMAGE_URL}/${component.image}`}
                                                                alt="Blog component"
                                                                style={{
                                                                    width: "100%",
                                                                    maxHeight:
                                                                        "180px",
                                                                    objectFit:
                                                                        "cover",
                                                                    borderRadius:
                                                                        "6px",
                                                                    marginTop:
                                                                        "10px",
                                                                }}
                                                            />
                                                        ) : (
                                                            <p>
                                                                No
                                                                image
                                                            </p>
                                                        )
                                                    ) : (
                                                        <div
                                                            className="admin-blog-component-body-preview"
                                                            dangerouslySetInnerHTML={{
                                                                __html:
                                                                    component.body ||
                                                                    "",
                                                            }}
                                                        />
                                                    )}
                                                </div>

                                                <div className="admin-user-actions">
                                                    <button
                                                        type="button"
                                                        className="admin-table-button"
                                                        disabled={
                                                            movingComponentId ===
                                                            component.id ||
                                                            !canMoveComponentUp(
                                                                index
                                                            )
                                                        }
                                                        onClick={() =>
                                                            handleMoveComponent(
                                                                component,
                                                                "up"
                                                            )
                                                        }
                                                    >
                                                        ↑
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="admin-table-button"
                                                        disabled={
                                                            movingComponentId ===
                                                            component.id ||
                                                            !canMoveComponentDown(
                                                                index
                                                            )
                                                        }
                                                        onClick={() =>
                                                            handleMoveComponent(
                                                                component,
                                                                "down"
                                                            )
                                                        }
                                                    >
                                                        ↓
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="admin-table-button"
                                                        onClick={() =>
                                                            handleEditComponent(
                                                                component
                                                            )
                                                        }
                                                    >
                                                        Edit
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="admin-table-button admin-table-button-danger"
                                                        disabled={
                                                            deletingComponentId ===
                                                            component.id
                                                        }
                                                        onClick={() =>
                                                            handleDeleteComponent(
                                                                component
                                                            )
                                                        }
                                                    >
                                                        {deletingComponentId ===
                                                        component.id
                                                            ? "Deleting..."
                                                            : "Delete"}
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    )
                                )}
                            </div>

                            {/* =================================================
                                COMPONENT EDITOR
                            ================================================= */}

                            <div className="admin-content-section">
                                <div className="admin-page-header">
                                    <div>
                                        <h3>
                                            {editingComponentId
                                                ? "Edit Component"
                                                : "Add Component"}
                                        </h3>
                                    </div>

                                    {editingComponentId && (
                                        <button
                                            type="button"
                                            className="admin-table-button"
                                            onClick={
                                                resetComponentForm
                                            }
                                        >
                                            Cancel Edit
                                        </button>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="blog-component-type">
                                        Type
                                    </label>

                                    <select
                                        id="blog-component-type"
                                        value={componentType}
                                        onChange={(e) =>
                                            setComponentType(
                                                e.target
                                                    .value as
                                                    | "image"
                                                    | "body"
                                            )
                                        }
                                    >
                                        <option value="body">
                                            Body
                                        </option>

                                        <option value="image">
                                            Image
                                        </option>
                                    </select>
                                </div>

                                {componentType ===
                                "image" ? (
                                    <div className="form-group">
                                        <label htmlFor="blog-component-image">
                                            Image
                                        </label>

                                        <input
                                            id="blog-component-image"
                                            type="file"
                                            accept="image/*,.svg"
                                            onChange={
                                                handleComponentImageChange
                                            }
                                        />

                                        {componentImagePreview && (
                                            <div className="admin-image-preview">
                                                <img
                                                    src={
                                                        componentImagePreview
                                                    }
                                                    alt="Component preview"
                                                    style={{
                                                        width: "160px",
                                                        maxHeight:
                                                            "120px",
                                                        objectFit:
                                                            "cover",
                                                        borderRadius:
                                                            "6px",
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="form-group">
                                        <label>
                                            Body
                                        </label>

                                        <RichTextEditor
                                            value={
                                                componentBody
                                            }
                                            onChange={
                                                setComponentBody
                                            }
                                            placeholder="Enter blog content..."
                                        />
                                    </div>
                                )}

                                <div className="admin-modal-actions">
                                    <button
                                        type="button"
                                        className="admin-primary-button"
                                        onClick={
                                            handleSaveComponent
                                        }
                                        disabled={
                                            savingComponent
                                        }
                                    >
                                        {savingComponent
                                            ? "Saving..."
                                            : editingComponentId
                                                ? "Update Component"
                                                : "Add Component"}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BlogModal;
